import React, { useState, useEffect } from "react";
import { Button, DialogContainer, FontIcon } from "react-md";

// screen components
import SelectPaymentMethod from "./states/SelectPaymentMethod";
import SelectShareLinkOptions from "./states/SelectShareLinkOptions";
import SuccessWithGeneratedLink from "./states/SuccessWithGeneratedLink";
import SuccessTotalPaymentWithWallet from "./states/SuccessTotalPaymentWithWallet";
import ErrorInCreatingAppointment from "./states/ErrorInCreatingAppointment";
import AptCreatedButLinkNotGenerated from "./states/AptCreatedButLinkNotGenerated.js";

import { SCREEN_STATE, PAYMENT_METHOD } from "./paymentLinkConstants";
import "./../../styles/PaymentLinkDialog.css";
import { formatCurrency } from "../../helper/formatCurrency";
import NavigoDashboardUtils from "../../services/NavigoDashboardUtils";
import EventService from "../../../../../../service/event/EventService";
import { CircularProgress } from "react-md";
import { APPOINTMENT_PAYMENT_STATUS } from "../../helper/appointmentConstants";

const PaymentLinkDialog = ({
  visible = false,
  onClose = () => {},
  appointment,
  withCreateAppointment = false,
  payloadForCreateAppointment = null,
  aptPrice = null,
  props,
  profileDetails = null,
  refreshAptDetailScreenData = () => {},
}) => {
  const [screenState, setScreenState] = useState(-1);
  const [paymentMethod, setPaymentMethod] = useState(-1);
  const [paymentLinkBody, setPaymentLinkBody] = useState(null);
  const [loading, setLoading] = useState(false);
  const [walletSubLimit, setWalletSubLimit] = useState(null);
  const [appointmentCreated, setAppointmentCreated] = useState(null);
  const [errTxtInCreatingApt, setErrTxtInCreatingApt] = useState("");

  const navigoDashboardUtils = new NavigoDashboardUtils();

  useEffect(() => {
    if (
      visible &&
      screenState === -1 &&
      (withCreateAppointment ||
        appointment?.paymentStatusResponse ===
          APPOINTMENT_PAYMENT_STATUS.PENDING)
    ) {
      if (withCreateAppointment && aptPrice === 0) {
        // directly create appointment without payment if price is 0
        createAppointment(
          payloadForCreateAppointment,
          SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET,
        );
      } else {
        checkCurrentWalletDetails();
      }
    } else if (
      visible &&
      screenState === -1 &&
      appointment?.paymentStatusResponse ===
        APPOINTMENT_PAYMENT_STATUS.INITIATED
    ) {
      setPaymentLinkBody(appointment?.paymentDetails?.paymentLink);
      setScreenState(SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK);
    }
  }, [visible, screenState]);

  const checkCurrentWalletDetails = () => {
    let requestBody = null;

    // Case 1: Create appointment flow
    if (withCreateAppointment) {
      setLoading(true);
      if (!payloadForCreateAppointment) {
        EventService.emit("showError", {
          message: "Appointment payload is required for creating appointment",
        });
        onClose();
        return;
      }

      if (aptPrice === null || aptPrice === undefined) {
        EventService.emit("showError", {
          message: "Appointment price is required",
        });
        onClose();
        return;
      }

      requestBody = {
        patientID: payloadForCreateAppointment?.patientID,
        bookedFor:
          payloadForCreateAppointment?.bookedFor == 0
            ? payloadForCreateAppointment?.patientID
            : payloadForCreateAppointment?.bookedFor,
        planID: payloadForCreateAppointment?.planID,
        serviceID: payloadForCreateAppointment?.serviceID,
        grandTotal: aptPrice,
      };
    }

    // Case 2: Existing appointment flow
    else if (
      appointment?.id &&
      appointment?.price > 0 &&
      appointment?.paymentStatusResponse === APPOINTMENT_PAYMENT_STATUS.PENDING
    ) {
      setWalletSubLimit(appointment?.paymentDetails?.paymentDetails);
      setScreenState(SCREEN_STATE.SELECT_PAYMENT_METHOD);
      setLoading(false);
      return;
    } else {
      EventService.emit("showError", {
        message: "Invalid appointment state",
      });
      onClose();
      return;
    }

    const onResponse = (res) => {
      setWalletSubLimit(res);
      setScreenState(SCREEN_STATE.SELECT_PAYMENT_METHOD);
      setLoading(false);
    };

    const onError = (err) => {
      setLoading(false);
      EventService.emit("showError", {
        message: `Error: ${err?.message || "Something went wrong"}`,
      });
      onClose();
    };

    navigoDashboardUtils.checkCurrentWalletDetails(
      requestBody,
      onResponse,
      onError,
    );
  };

  const createAppointment = (body, screenNo) => {
    setLoading(true);
    const onResponse = (res) => {
      setLoading(false);
      EventService.emit("showSuccess", {
        message: "Appointment created and payment link generated successfully",
      });
      setAppointmentCreated(res);
      if (aptPrice === 0) {
        setScreenState(screenNo);
        return;
      } else {
        const paymentLink = res?.paymentDetails?.paymentLink;

        if (
          screenNo === SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK &&
          !paymentLink
        ) {
          setScreenState(
            SCREEN_STATE.APPOINTMENT_CREATED_BUT_LINK_NOT_GENERATED,
          );
          return;
        }
        setScreenState(screenNo);
        setPaymentLinkBody(paymentLink);
      }
    };
    const onError = (err) => {
      setLoading(false);
      setScreenState(SCREEN_STATE.ERROR_CREATING_APPOINTMENT);
      setErrTxtInCreatingApt(err?.message || "");
      EventService.emit("showError", {
        message: `Error: ${err?.message}`,
      });
    };
    navigoDashboardUtils.createAppointment(body, onResponse, onError);
  };

  const goNext = (screenNo, paymentLink) => {
    if (screenNo === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS) {
      setScreenState(SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS);
      return;
    }

    const isWalletUsed = paymentMethod !== PAYMENT_METHOD.RAZORPAY_ONLY;

    if (
      (screenNo === SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK && paymentLink) ||
      screenNo === SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET
    ) {
      // If creating appointment flow, call createAppointment instead
      if (withCreateAppointment) {
        if (!payloadForCreateAppointment) {
          EventService.emit("showError", {
            message: "Appointment payload is required for creating appointment",
          });
          onClose();
          return;
        }

        // Merge the appointment body with payment link options
        const paymentLinkRequest = {
          paymentDetails: {
            ...walletSubLimit,
          },
          useWallet: isWalletUsed,
        };

        // Only include paymentLink if it's provided (not for WALLET_ONLY payments)
        if (paymentLink) {
          paymentLinkRequest.paymentLink = paymentLink;
        }

        const body = {
          ...payloadForCreateAppointment,
          paymentLinkRequest,
        };
        createAppointment(body, screenNo);
        return;
      } else {
        generatePaymentLink(isWalletUsed, screenNo, paymentLink);
      }
    }
  };

  const generatePaymentLink = (isWalletUsed, screenNo, paymentLink = null) => {
    if (!appointment?.id) {
      EventService.emit("showError", {
        message: "Appointment not found",
      });
      onClose();
      return;
    }
    setLoading(true);

    const onResponse = (res) => {
      setLoading(false);
      setScreenState(screenNo);
      setPaymentLinkBody(res?.paymentLink);
      refreshAptDetailScreenData();
    };

    const onError = (err) => {
      setLoading(false);
      EventService.emit("showError", {
        message: `Error: ${err?.message || "Something went wrong"}`,
      });
      onClose();
    };

    const requestBody = {
      appointmentId: appointment.id,
      useWallet: isWalletUsed,
    };

    // Only include paymentLink if it's provided (not for WALLET_ONLY payments)
    if (paymentLink) {
      requestBody.paymentLink = paymentLink;
    }

    navigoDashboardUtils.generatePaymentLink(requestBody, onResponse, onError);
  };

  return (
    <DialogContainer
      id="payment-link-dialog"
      visible={visible}
      onHide={() => {}}
      focusOnMount={false}
      autosizeContent
      width={
        screenState === SCREEN_STATE.SELECT_PAYMENT_METHOD ||
        screenState === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS
          ? "720px"
          : "550px"
      }
      dialogStyle={{ borderRadius: "10px", minHeight: "400px" }}
    >
      {loading ? (
        <div
          style={{
            marginTop: "160px",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div className="payment-link-dialog-container zy-text-container">
          {/* Header */}
          {(screenState === SCREEN_STATE.SELECT_PAYMENT_METHOD ||
            screenState === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS) && (
            <>
              <div className="pld-header">
                <div className="pld-header-title">Proceed to pay</div>

                <FontIcon className="pld-close-icon" onClick={onClose}>
                  close
                </FontIcon>
              </div>
              {/* Grand Total */}
              <div className="pld-total-wrapper">
                <div className="pld-total-box">
                  Grand total:{" "}
                  <span className="pld-bold">
                    {formatCurrency(walletSubLimit?.grandTotal)}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Dynamic screens */}
          {screenState === SCREEN_STATE.SELECT_PAYMENT_METHOD && (
            <SelectPaymentMethod
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              goNext={goNext}
              walletSubLimit={walletSubLimit}
              withCreateAppointment={withCreateAppointment}
            />
          )}

          {screenState === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS && (
            <SelectShareLinkOptions
              goNext={goNext}
              profileDetails={profileDetails}
              withCreateAppointment={withCreateAppointment}
            />
          )}

          {screenState === SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK && (
            <SuccessWithGeneratedLink
              onClose={onClose}
              paymentLinkBody={paymentLinkBody}
              withCreateAppointment={withCreateAppointment}
              appointmentCreated={appointmentCreated}
              appointment={appointment}
              props={props}
            />
          )}

          {screenState === SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET && (
            <SuccessTotalPaymentWithWallet
              onClose={onClose}
              withCreateAppointment={withCreateAppointment}
              appointmentCreated={appointmentCreated}
              props={props}
            />
          )}
          {screenState === SCREEN_STATE.ERROR_CREATING_APPOINTMENT && (
            <ErrorInCreatingAppointment
              onClose={() => {
                onClose();
                setScreenState(-1);
              }}
              errorText={errTxtInCreatingApt}
            />
          )}

          {screenState ===
            SCREEN_STATE.APPOINTMENT_CREATED_BUT_LINK_NOT_GENERATED && (
            <AptCreatedButLinkNotGenerated
              appointmentCreated={appointmentCreated}
              props={props}
            />
          )}
        </div>
      )}
    </DialogContainer>
  );
};

export default PaymentLinkDialog;
